import { z } from 'zod';

/**
 * Dale Agents (Ultra): the receptionist's settings, the runtime controls and
 * the Bandeja. Shapes follow docs/backend-api-contract.v1.md, "Dale Agents
 * runtime". A 503 with code `agents_unavailable` means the runtime is not
 * configured yet or did not answer; the settings still read and save.
 */

export const PausedReasonSchema = z.enum(['owner', 'cap']);

export const ReceptionistSettingsSchema = z.object({
  instructions: z.string(),
  tone: z.string(),
  canBook: z.boolean(),
  handoffPhone: z.string().nullable(),
  paused: z.boolean(),
  pausedReason: PausedReasonSchema.nullable(),
  monthlyCapCents: z.number().int(),
  updatedAt: z.string().nullable(),
  /** Versiones contra choques (0099): the document's version, 0 until it is first saved. Sent back as expectedVersion. */
  version: z.number().int().nonnegative().optional(),
});
export type ReceptionistSettings = z.infer<typeof ReceptionistSettingsSchema>;

export type ReceptionistEdit = Pick<ReceptionistSettings, 'instructions' | 'tone' | 'canBook' | 'handoffPhone'> & {
  /** The version the form read; a stale one comes back 409 version_conflict. */
  expectedVersion?: number;
};

export const ReceptionistResponseSchema = z.object({ data: ReceptionistSettingsSchema });
/** `synced: false` means it saved, but the runtime has not picked it up yet. */
export const SaveReceptionistResponseSchema = z.object({ data: ReceptionistSettingsSchema, synced: z.boolean() });
export type SaveReceptionistResponse = z.infer<typeof SaveReceptionistResponseSchema>;

export const WhatsAppPairingSchema = z.object({ qr: z.string(), expiresAt: z.string() });
export type WhatsAppPairing = z.infer<typeof WhatsAppPairingSchema>;

export const CodexLoginSchema = z.object({ verificationUrl: z.string(), userCode: z.string(), expiresAt: z.string() });
export type CodexLogin = z.infer<typeof CodexLoginSchema>;

export const AgentRuntimeStatusSchema = z.object({
  whatsapp: z.enum(['unlinked', 'pairing', 'linked']),
  codex: z.enum(['none', 'pending', 'ready']),
  fallback: z.boolean(),
  paused: z.boolean(),
  lastTurnAt: z.string().nullable(),
});

export const AgentUsageSchema = z.object({
  period: z.string(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  usedCents: z.number().int(),
  capCents: z.number().int(),
  capReached: z.boolean(),
  resetsAt: z.string(),
  takenAt: z.string().nullable(),
});

export const AgentRuntimeSchema = z.object({
  status: AgentRuntimeStatusSchema,
  paused: z.boolean(),
  pausedReason: PausedReasonSchema.nullable(),
  usage: AgentUsageSchema,
});
export type AgentRuntime = z.infer<typeof AgentRuntimeSchema>;

export const ConversationStatusSchema = z.enum(['bot', 'human', 'closed']);
export type ConversationStatus = z.infer<typeof ConversationStatusSchema>;

export const AgentMessageSchema = z.object({
  id: z.string(),
  direction: z.enum(['in', 'out']),
  text: z.string(),
  at: z.string(),
  source: z.enum(['agent', 'customer', 'staff']),
});
export type AgentMessage = z.infer<typeof AgentMessageSchema>;

export const AgentConversationSchema = z.object({
  id: z.string(),
  customerPhone: z.string(),
  customerName: z.string().nullable(),
  status: ConversationStatusSchema,
  handoffReason: z.string().nullable(),
  lastMessageAt: z.string(),
});
export type AgentConversation = z.infer<typeof AgentConversationSchema>;

export const AgentConversationSummarySchema = AgentConversationSchema.extend({
  lastMessage: AgentMessageSchema.pick({ text: true, direction: true, source: true, at: true }).nullable(),
});
export type AgentConversationSummary = z.infer<typeof AgentConversationSummarySchema>;

export const ConversationListResponseSchema = z.object({
  data: z.array(AgentConversationSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ConversationListResponse = z.infer<typeof ConversationListResponseSchema>;

export const ConversationThreadSchema = z.object({
  conversation: AgentConversationSchema,
  messages: z.array(AgentMessageSchema),
});
export type ConversationThread = z.infer<typeof ConversationThreadSchema>;
